import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment/moment";
import type { CrudType } from "../utils/useCrud/CrudType";
import { CrudSend } from "./useCrudV2/crudSend";
import { getUseDelete } from "./useCrudV2/getUseDelete";
import { getUsePost } from "./useCrudV2/getUsePost";
import { getUsePut } from "./useCrudV2/getUsePut";

export type DbComment = {
  id: number;
  externalCommentId: string;
  eventId: number;
  content: string;
  createdAt: Date;
  createdBy: number;
};

type PostRequest = {
  eventId: number;
  content: string;
};

type PutRequest = {
  // We need eventId to set the queryKey.
  eventId: number;
  externalCommentId: string;
  content: string;
} & CrudType;

type DeleteRequest = {
  // We need to have "id" in the type to satisfy the CrudType constraint.
  id: number;
  // We need eventId to set the queryKey.
  eventId: number;
  externalCommentId: string;
};

type Response = Omit<DbComment, "createdAt"> & {
  // Postgres string date
  createdAt: string;
};

const parse = (response: Response): DbComment => ({
  ...response,
  createdAt: moment(response.createdAt).toDate(),
});

const commentCrud = new CrudSend<Response, PostRequest, PutRequest, DbComment>(parse);

export const useCommentsQuery = (eventId?: number, hasComments?: boolean) => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["comments", eventId],
    () => commentCrud.getCollection(eventId ? `/events/${eventId}/comments` : undefined),
    {
      enabled: eventId !== undefined && hasComments === true,
    }
  );

  // Check if there are any pending comments in the cache for this event
  const pendingComments = eventId
    ? (queryClient.getQueryData(["comments", eventId]) as DbComment[] | undefined)
    : undefined;
  const hasPendingComments = pendingComments && pendingComments.length > 0;

  // For the case where hasComments is false, we need to ensure we return the same structure but with empty data and no loading state.
  // However, if there are pending comments, we should show those instead of empty data.
  return {
    ...query,
    data: hasComments === false ? (hasPendingComments ? pendingComments : []) : query.data,
    isLoading: hasComments === false ? false : query.isLoading,
    isSuccess: hasComments === false ? true : query.isSuccess,
    status: hasComments === false ? ("success" as const) : query.status,
  };
};

export const useCommentPost = getUsePost<PostRequest, DbComment>(
  (item) => ["comments", item.eventId],
  (body) => commentCrud.post(`/events/${body.eventId}/comments`, body),
  ({ id, ...item }, user) => ({
    ...item,
    id,
    externalCommentId: id.toString(),
    createdAt: new Date(),
    createdBy: user?.id,
  })
);

export const useCommentPut = getUsePut<PutRequest, DbComment>(
  (item) => ["comments", item.eventId],
  (body) => commentCrud.put(`/comments/${body.externalCommentId}`, body)
);

export const useCommentDelete = getUseDelete<DeleteRequest, DbComment>(
  (item) => ["comments", item.eventId],
  ({ externalCommentId }) => commentCrud.delete(`/comments/${externalCommentId}`),
  "externalCommentId"
);
